import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, Clock, MapPin, Heart, Plus, Minus, ShoppingBag, Loader2, X } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

const QUICK_TOGGLES = ["Ντομάτα", "Κρεμμύδι", "Iceberg", "Μουστάρδα", "Ketchup", "Mayo", "Τυρί"];

export const StoreMenu = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { add, items, count, subtotal, setQty, setOpen: openCart } = useCart();
    const [store, setStore] = useState(null);
    const [activeCategory, setActiveCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeItem, setActiveItem] = useState(null);

    useEffect(() => {
        api.get(`/stores/${slug}`).then(({ data }) => {
            setStore(data);
            const firstCat = (data.menu || [])[0]?.category;
            setActiveCategory(firstCat);
            setLoading(false);
        }).catch(() => { setLoading(false); navigate("/"); });
    }, [slug, navigate]);

    const categories = useMemo(() => {
        if (!store) return [];
        return [...new Set((store.menu || []).map((m) => m.category))];
    }, [store]);

    const filteredItems = useMemo(() => {
        if (!store) return [];
        return (store.menu || []).filter((m) => m.category === activeCategory);
    }, [store, activeCategory]);

    if (loading) {
        return (
            <div className="cyber-bg min-h-screen grid place-items-center text-white/40">
                <Loader2 className="animate-spin" size={28} />
            </div>
        );
    }
    if (!store) return null;

    return (
        <div className="cyber-bg min-h-screen text-white pb-32" data-testid="store-menu">
            {/* Hero */}
            <div className="relative h-56">
                <img src={store.image_url} alt={store.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0F] via-[#0B0B0F]/70 to-transparent" />
                <button
                    onClick={() => navigate("/")}
                    data-testid="store-back"
                    className="absolute top-3 left-3 h-10 w-10 grid place-items-center rounded-full glass-gold"
                >
                    <ArrowLeft size={16} />
                </button>
                <button className="absolute top-3 right-3 h-10 w-10 grid place-items-center rounded-full glass-gold">
                    <Heart size={16} />
                </button>
                <div className="absolute bottom-3 left-4 right-4">
                    <h1 className="font-heading text-3xl" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{store.name}</h1>
                    <p className="text-[11px] text-white/60 capitalize">{store.cuisine} · {store.tagline}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs">
                        <span className="flex items-center gap-1 text-[var(--gold-light)]">
                            <Star size={12} fill="currentColor" />
                            <span className="font-bold">{store.rating.toFixed(1)}</span>
                            <span className="text-white/50 text-[10px]">({store.rating_count})</span>
                        </span>
                        <span className="flex items-center gap-1 text-white/60">
                            <Clock size={12} />{store.delivery_minutes}'
                        </span>
                        <span className="flex items-center gap-1 text-white/60">
                            <MapPin size={12} />
                            {store.location.address}
                        </span>
                    </div>
                </div>
            </div>

            {/* Category tabs */}
            <div className="sticky top-0 z-30 glass-gold border-b border-[var(--gold)]/20 px-4 py-3 -mt-1">
                <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            data-testid={`menu-cat-${cat}`}
                            onClick={() => setActiveCategory(cat)}
                            className={`pill-chip ${activeCategory === cat ? "active" : ""}`}
                        >
                            {cat.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {/* Menu items */}
            <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
                {filteredItems.map((it) => (
                    <ItemCard key={it.id} item={it} onOpen={() => setActiveItem(it)} />
                ))}
            </div>

            {/* Sticky cart bar */}
            {count > 0 && (
                <div className="fixed bottom-0 left-0 right-0 z-40 glass-gold border-t border-[var(--gold)]/30 p-3" data-testid="store-sticky-cart">
                    <button
                        onClick={() => openCart(true)}
                        className="gold-cta w-full h-14 rounded-2xl flex items-center justify-between px-5 font-heading uppercase tracking-wider text-sm font-black"
                    >
                        <span className="flex items-center gap-2">
                            <ShoppingBag size={18} strokeWidth={2.5} />
                            {count} {count === 1 ? "προϊόν" : "προϊόντα"}
                        </span>
                        <span>€{subtotal.toFixed(2)}</span>
                    </button>
                </div>
            )}

            {/* Quick add modal */}
            {activeItem && (
                <ItemModal item={activeItem} onClose={() => setActiveItem(null)} onAdd={add} />
            )}
        </div>
    );
};

const ItemCard = ({ item, onOpen }) => (
    <article
        data-testid={`menu-item-${item.id}`}
        className="glass-gold rounded-2xl overflow-hidden flex flex-col group cursor-pointer hover:border-[var(--gold)]/60 transition"
        onClick={onOpen}
    >
        <div className="relative aspect-[16/9] overflow-hidden">
            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
            {item.featured && (
                <span className="absolute top-2 left-2 text-[8px] font-bold uppercase tracking-widest border border-[var(--gold)] text-[var(--gold-light)] bg-black/70 px-2 py-0.5 rounded-full">★ Top</span>
            )}
        </div>
        <div className="p-3 flex-1 flex flex-col">
            <h3 className="font-heading uppercase text-base leading-tight">{item.name}</h3>
            <p className="text-[11px] text-white/55 line-clamp-2 mt-0.5 flex-1">{item.description}</p>
            <div className="flex items-center justify-between mt-2.5">
                <span className="font-mono font-bold text-[var(--gold-light)] text-base">€{item.price.toFixed(2)}</span>
                <button
                    onClick={(e) => { e.stopPropagation(); onOpen(); }}
                    data-testid={`menu-add-${item.id}`}
                    className="h-9 w-9 grid place-items-center rounded-full bg-gradient-to-br from-[var(--gold-light)] to-[var(--gold)] text-[#0B0B0F] active:scale-90 transition"
                >
                    <Plus size={18} strokeWidth={3} />
                </button>
            </div>
        </div>
    </article>
);

const ItemModal = ({ item, onClose, onAdd }) => {
    const [qty, setQty] = useState(1);
    const [removed, setRemoved] = useState([]);

    const itemToggles = useMemo(() => {
        const desc = (item.description || "").toLowerCase();
        return QUICK_TOGGLES.filter((t) => desc.includes(t.toLowerCase()));
    }, [item]);

    const total = item.price * qty;

    const submit = () => {
        const suffix = removed.length ? ` · χωρίς ${removed.join(", ")}` : "";
        const id = removed.length ? `${item.id}-no-${removed.join("_").replace(/\s+/g, "")}` : item.id;
        for (let i = 0; i < qty; i++) {
            onAdd({ id, name: item.name + suffix, price: item.price, image_url: item.image_url, category: item.category }, { silent: true });
        }
        toast.success(`${qty}× ${item.name} στο καλάθι.`);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center" onClick={onClose} data-testid="item-modal">
            <div className="cyber-bg w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border-t sm:border border-[var(--gold)]/30 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-3 right-3 z-10 h-10 w-10 grid place-items-center rounded-full bg-black/70" data-testid="item-modal-close">
                    <X size={16} />
                </button>
                <div className="relative aspect-[16/10]">
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0F] via-transparent" />
                    <h2 className="absolute bottom-3 left-4 right-4 font-heading text-3xl" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{item.name}</h2>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-sm text-white/70 leading-relaxed">{item.description}</p>

                    {itemToggles.length > 0 && (
                        <div>
                            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--gold-light)] mb-2">Αφαίρεσε υλικά</p>
                            <div className="grid grid-cols-2 gap-2">
                                {itemToggles.map((t) => {
                                    const on = removed.includes(t);
                                    return (
                                        <button
                                            key={t}
                                            data-testid={`item-toggle-${t}`}
                                            onClick={() => setRemoved((curr) => on ? curr.filter((x) => x !== t) : [...curr, t])}
                                            className={`flex items-center gap-2 px-3 h-12 rounded-xl border-2 text-left text-sm transition active:scale-95 ${on ? "border-[var(--gold)] bg-[var(--gold)]/10 text-[var(--gold-light)]" : "border-white/10 text-white/75"}`}
                                        >
                                            <span className={`h-5 w-5 grid place-items-center rounded border-2 ${on ? "border-[var(--gold)] bg-[var(--gold)]/30" : "border-white/30"}`}>
                                                {on && <X size={12} strokeWidth={3} />}
                                            </span>
                                            <span className={on ? "line-through" : ""}>{t}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-3 pt-2">
                        <div className="flex items-center gap-1 border border-white/15 rounded-full p-1 shrink-0">
                            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-9 w-9 grid place-items-center rounded-full hover:bg-white/10" data-testid="item-qty-dec">
                                <Minus size={14} />
                            </button>
                            <span className="w-7 text-center font-bold" data-testid="item-qty">{qty}</span>
                            <button onClick={() => setQty((q) => q + 1)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-white/10" data-testid="item-qty-inc">
                                <Plus size={14} />
                            </button>
                        </div>
                        <button
                            data-testid="item-add-btn"
                            onClick={submit}
                            className="gold-cta flex-1 h-12 rounded-full flex items-center justify-between px-5 font-heading uppercase tracking-wider text-xs font-black"
                        >
                            <span>Στο καλάθι</span>
                            <span>€{total.toFixed(2)}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
